import { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter,
} from "../components/ui/dialog";
import { Badge } from "../components/ui/badge";
import {
  Trophy, Users, DollarSign, Clock, Calendar, Image as ImageIcon, Lock, Award,
  CheckCircle2, Plus, Frown,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Parse "YYYY-MM-DDTHH:MM" como horario de Brasilia (GMT-3) e retorna timestamp ms
function parseBrasiliaToMs(s) {
  if (!s) return null;
  const m = String(s).match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/);
  if (!m) return new Date(s).getTime();
  const [, Y, M, D, h, mn] = m;
  // Date.UTC retorna ms UTC. Adicionando +3h converte de "horario Brasilia" para UTC real.
  return Date.UTC(Number(Y), Number(M) - 1, Number(D), Number(h) + 3, Number(mn));
}

const fmtCurrency = (v) =>
  new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(v || 0);

function useCountdown(targetIso) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  if (!targetIso) return null;
  const target = parseBrasiliaToMs(targetIso);
  if (!target) return null;
  const diff = target - now;
  if (diff <= 0) return { expired: true, d: 0, h: 0, m: 0, s: 0 };
  const d = Math.floor(diff / (1000 * 60 * 60 * 24));
  const h = Math.floor((diff / (1000 * 60 * 60)) % 24);
  const m = Math.floor((diff / (1000 * 60)) % 60);
  const s = Math.floor((diff / 1000) % 60);
  return { expired: false, d, h, m, s };
}

export default function BolaoPublicoPage() {
  const { codigo } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [dlgPart, setDlgPart] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ nome: "", whatsapp: "", palpites: [] });

  const fetchData = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/bolao/publico/${codigo}`);
      setData(res.data);
    } catch (e) {
      if (e?.response?.status === 404) setNotFound(true);
    } finally {
      setLoading(false);
    }
  }, [codigo]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const countdown = useCountdown(data?.encerramento);
  const isEncerrado = data?.encerrado || countdown?.expired;
  const showResultado = data?.resultado_informado;
  const palpiteAtivo = data?.palpite_publico_ativo !== false;

  const openParticipar = () => {
    setForm({
      nome: "",
      whatsapp: "",
      palpites: (data.jogos || []).map(j => ({ jogo_id: j.id, gols1: 0, gols2: 0 })),
    });
    setDlgPart(true);
  };

  const submitParticipar = async () => {
    if (!form.nome.trim()) { toast.error("Informe seu nome"); return; }
    const wpp = (form.whatsapp || "").replace(/\D/g, "");
    if (wpp.length < 10) { toast.error("Informe um WhatsApp válido"); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/bolao/publico/${codigo}/participantes`, {
        nome: form.nome.trim(),
        whatsapp: form.whatsapp || "",
        valor_pago: data.valor_palpite || 0,
        pago: false,
        palpites: form.palpites.map(p => ({
          jogo_id: p.jogo_id,
          gols1: Number(p.gols1) || 0,
          gols2: Number(p.gols2) || 0,
        })),
      });
      toast.success("Palpite registrado!");
      setDlgPart(false);
      fetchData();
    } catch (e) {
      const msg = e?.response?.data?.detail || "Erro ao registrar palpite";
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="w-10 h-10 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (notFound || !data) {
    return (
      <div className="min-h-screen bg-background flex flex-col items-center justify-center p-6 text-center">
        <Frown className="w-16 h-16 text-muted-foreground mb-4" />
        <h1 className="text-2xl font-bold mb-2">Bolão não encontrado</h1>
        <p className="text-muted-foreground">O link pode estar incorreto ou o bolão foi removido.</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background py-6 md:py-10 px-4">
      <div className="max-w-4xl mx-auto space-y-6" data-testid="bolao-publico-page">
        {/* Hero */}
        <div className="text-center space-y-2">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-primary/15 mb-2">
            <Trophy className="w-8 h-8 text-primary" />
          </div>
          <h1 className="text-3xl md:text-4xl font-bold" data-testid="bolao-titulo">{data.nome}</h1>
          <p className="text-sm text-muted-foreground">Código: {data.codigo_publico}</p>
        </div>

        {/* Countdown ou Encerrado */}
        {showResultado ? (
          <Card className="border-amber-500/40 bg-amber-500/10">
            <CardContent className="p-4 text-center">
              <p className="text-amber-400 font-semibold flex items-center justify-center gap-2">
                <Award className="w-5 h-5" /> Resultado Oficial Divulgado
              </p>
            </CardContent>
          </Card>
        ) : isEncerrado ? (
          <Card className="border-red-500/40 bg-red-500/10">
            <CardContent className="p-4 text-center">
              <p className="text-red-400 font-semibold flex items-center justify-center gap-2">
                <Lock className="w-5 h-5" /> Bolão Encerrado - Aguardando Resultado
              </p>
            </CardContent>
          </Card>
        ) : countdown && (
          <Card className="border-primary/30 bg-primary/5">
            <CardContent className="p-4">
              <p className="text-xs text-muted-foreground text-center mb-2 flex items-center justify-center gap-1">
                <Clock className="w-3.5 h-3.5" /> Tempo restante para palpitar
              </p>
              <div className="grid grid-cols-4 gap-2 text-center" data-testid="countdown">
                {[
                  { label: "Dias", v: countdown.d },
                  { label: "Horas", v: countdown.h },
                  { label: "Min", v: countdown.m },
                  { label: "Seg", v: countdown.s },
                ].map((it) => (
                  <div key={it.label} className="bg-card/80 rounded-lg p-3">
                    <p className="text-2xl md:text-3xl font-bold text-primary tabular-nums">{String(it.v).padStart(2, "0")}</p>
                    <p className="text-xs text-muted-foreground">{it.label}</p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Info Cards */}
        <div className="grid grid-cols-3 gap-3">
          <Card><CardContent className="p-4 text-center">
            <DollarSign className="w-5 h-5 mx-auto text-primary mb-1" />
            <p className="text-xs text-muted-foreground">Valor do Palpite</p>
            <p className="text-lg font-bold">{fmtCurrency(data.valor_palpite)}</p>
          </CardContent></Card>
          <Card><CardContent className="p-4 text-center">
            <Users className="w-5 h-5 mx-auto text-blue-400 mb-1" />
            <p className="text-xs text-muted-foreground">Participantes</p>
            <p className="text-lg font-bold">{data.qtd_participantes}</p>
          </CardContent></Card>
          <Card><CardContent className="p-4 text-center">
            <Trophy className="w-5 h-5 mx-auto text-emerald-400 mb-1" />
            <p className="text-xs text-muted-foreground">Arrecadado</p>
            <p className="text-lg font-bold text-emerald-400">{fmtCurrency(data.valor_arrecadado)}</p>
          </CardContent></Card>
        </div>

        {/* CTA Participar */}
        {!isEncerrado && !showResultado && palpiteAtivo && (
          <Button onClick={openParticipar} size="lg" className="w-full" data-testid="participar-btn">
            <Plus className="w-5 h-5 mr-2" /> Fazer Meu Palpite
          </Button>
        )}

        {/* Ganhadores (se finalizado) */}
        {showResultado && (
          <Card className="border-amber-500/40 bg-gradient-to-br from-amber-500/10 to-transparent">
            <CardContent className="p-4 md:p-6">
              <h2 className="text-xl md:text-2xl font-bold mb-4 flex items-center gap-2 text-amber-400">
                <Award className="w-6 h-6" /> Ganhadores
              </h2>
              {(data.ganhadores || []).length === 0 ? (
                <div className="text-center py-6">
                  <Frown className="w-10 h-10 mx-auto text-muted-foreground mb-2" />
                  <p className="text-muted-foreground font-medium">Não houve ganhadores neste bolão.</p>
                </div>
              ) : (
                <>
                  <div className="mb-4 p-3 rounded-lg bg-amber-500/15 border border-amber-500/30 text-center">
                    <p className="text-xs text-amber-300/80 mb-0.5">
                      {data.ganhadores.length > 1 ? `Prêmio dividido entre ${data.ganhadores.length} ganhadores` : "Prêmio total para o ganhador"}
                    </p>
                    <p className="text-2xl font-bold text-amber-400" data-testid="premio-por-ganhador">
                      {fmtCurrency(data.premio_por_ganhador)} <span className="text-sm text-amber-300/70 font-normal">por ganhador</span>
                    </p>
                  </div>
                  <div className="space-y-3">
                    {data.ganhadores.map((g, idx) => (
                      <div key={idx} className="border border-amber-500/30 rounded-lg p-3 bg-card/60" data-testid={`ganhador-${idx}`}>
                        <div className="flex items-center justify-between mb-2 gap-2 flex-wrap">
                          <p className="font-semibold flex items-center gap-2">
                            <Trophy className="w-4 h-4 text-amber-400" /> {g.nome}
                          </p>
                          <div className="flex items-center gap-2">
                            <Badge className="bg-emerald-500/15 text-emerald-400 border-emerald-500/30 font-bold">
                              {fmtCurrency(data.premio_por_ganhador)}
                            </Badge>
                            <Badge className="bg-amber-500/15 text-amber-400 border-amber-500/30">
                              {g.acertos}/{data.total_jogos} acertos
                            </Badge>
                          </div>
                        </div>
                        <div className="text-xs space-y-0.5 text-muted-foreground">
                          {(g.palpites || []).map((pl, i) => {
                            const j = data.jogos.find(x => x.id === pl.jogo_id);
                            return j ? <div key={i}>{j.time1} <span className="text-amber-400 font-semibold">{pl.gols1} x {pl.gols2}</span> {j.time2}</div> : null;
                          })}
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        )}

        {/* Jogos */}
        <Card>
          <CardContent className="p-4 md:p-6">
            <h2 className="font-bold text-lg mb-4 flex items-center gap-2">
              <Calendar className="w-5 h-5" /> Jogos {showResultado && <span className="text-amber-400 text-sm font-normal">(Resultado Oficial)</span>}
            </h2>
            <div className="grid gap-3 md:grid-cols-2">
              {(data.jogos || []).map((j, idx) => (
                <div key={j.id} className="border border-border/60 rounded-lg p-3 bg-card/60" data-testid={`jogo-pub-${idx}`}>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs text-muted-foreground">Jogo {idx + 1}</span>
                    <span className="text-xs text-muted-foreground flex items-center gap-1">
                      <Clock className="w-3 h-3" /> {j.data} {j.hora}
                    </span>
                  </div>
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2 flex-1 min-w-0">
                      {j.logo1 ? <img src={j.logo1} alt={j.time1} className="w-10 h-10 object-contain" onError={(e) => e.target.style.display='none'} /> : <ImageIcon className="w-10 h-10 text-muted-foreground/40" />}
                      <span className="font-medium truncate">{j.time1}</span>
                    </div>
                    <div className="text-center px-2 min-w-[60px]">
                      {showResultado && j.gols1 != null && j.gols2 != null ? (
                        <span className="font-bold text-amber-400 text-lg">{j.gols1} x {j.gols2}</span>
                      ) : (
                        <span className="text-muted-foreground text-sm">vs</span>
                      )}
                    </div>
                    <div className="flex items-center gap-2 flex-1 min-w-0 justify-end">
                      <span className="font-medium truncate text-right">{j.time2}</span>
                      {j.logo2 ? <img src={j.logo2} alt={j.time2} className="w-10 h-10 object-contain" onError={(e) => e.target.style.display='none'} /> : <ImageIcon className="w-10 h-10 text-muted-foreground/40" />}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Participantes */}
        <Card>
          <CardContent className="p-4 md:p-6">
            <h2 className="font-bold text-lg mb-4 flex items-center gap-2">
              <Users className="w-5 h-5" /> Participantes ({data.qtd_participantes})
            </h2>
            {(data.participantes || []).length === 0 ? (
              <p className="text-center text-muted-foreground py-6 text-sm">
                Seja o primeiro a palpitar!
              </p>
            ) : (
              <div className="space-y-2">
                {data.participantes.map((p) => (
                  <div key={p.id} className="border border-border/40 rounded-lg p-3 bg-card/40">
                    <p className="font-semibold mb-1.5 flex items-center gap-2">
                      <CheckCircle2 className="w-4 h-4 text-emerald-400" /> {p.nome}
                    </p>
                    <div className="text-xs grid sm:grid-cols-2 gap-x-3 gap-y-0.5 text-muted-foreground">
                      {(p.palpites || []).map((pl, i) => {
                        const j = data.jogos.find(x => x.id === pl.jogo_id);
                        return j ? <div key={i}>{j.time1} <span className="text-foreground font-medium">{pl.gols1} x {pl.gols2}</span> {j.time2}</div> : null;
                      })}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        <p className="text-center text-xs text-muted-foreground py-4">
          Powered by NexPlayerTV • Sistema de Bolão
        </p>
      </div>

      {/* Dialog: Participar */}
      <Dialog open={dlgPart} onOpenChange={setDlgPart}>
        <DialogContent className="sm:max-w-md max-h-[90vh] overflow-y-auto" data-testid="dialog-participar">
          <DialogHeader>
            <DialogTitle>Fazer Meu Palpite</DialogTitle>
            <DialogDescription>Preencha seu nome e palpite cada jogo.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label>Seu nome *</Label>
              <Input value={form.nome} onChange={(e) => setForm(f => ({ ...f, nome: e.target.value }))} placeholder="Seu nome completo" data-testid="pub-nome" />
            </div>
            <div className="space-y-1.5">
              <Label>WhatsApp *</Label>
              <Input value={form.whatsapp} onChange={(e) => setForm(f => ({ ...f, whatsapp: e.target.value }))} placeholder="(00) 00000-0000" required data-testid="pub-whatsapp" />
            </div>
            <div className="border-t border-border/60 pt-3 space-y-2">
              <p className="text-sm font-semibold">Seus Palpites</p>
              {form.palpites.map((pl, idx) => {
                const j = data.jogos.find(x => x.id === pl.jogo_id);
                return (
                  <div key={pl.jogo_id} className="grid grid-cols-[1fr_50px_15px_50px_1fr] items-center gap-2 text-sm">
                    <span className="truncate">{j?.time1}</span>
                    <Input type="number" min="0" value={pl.gols1} onChange={(e) => setForm(f => ({
                      ...f, palpites: f.palpites.map((p, i) => i === idx ? { ...p, gols1: e.target.value } : p)
                    }))} className="h-9 text-center" data-testid={`pub-palpite-${idx}-g1`} />
                    <span className="text-center text-muted-foreground">x</span>
                    <Input type="number" min="0" value={pl.gols2} onChange={(e) => setForm(f => ({
                      ...f, palpites: f.palpites.map((p, i) => i === idx ? { ...p, gols2: e.target.value } : p)
                    }))} className="h-9 text-center" data-testid={`pub-palpite-${idx}-g2`} />
                    <span className="truncate text-right">{j?.time2}</span>
                  </div>
                );
              })}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDlgPart(false)}>Cancelar</Button>
            <Button onClick={submitParticipar} disabled={saving} data-testid="pub-save-btn">
              {saving ? "Enviando..." : "Confirmar Palpite"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
